import React, { useEffect, useState } from 'react';
import { useOidc } from '@axa-fr/react-oidc';
import { ButtonV2, CoreButtonTypes } from '@metaforcelabs/metaforce-core';
import { getCompaniesForMe, setCompany } from '../../api/company';
import { useErrorHandler } from '../../hooks/useErrorHandler';

// Matches CustomerActivationStatus.Pending in the API (Metaforce.CenterPoint.Models.Enums).
const ACTIVATION_STATUS_PENDING = 1;

export default function SelectCompany() {
  const errorHandler = useErrorHandler();
  const { renewTokens } = useOidc();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSelect = async (customerId) => {
    setSubmitting(true);
    try {
      await setCompany(customerId);
      // renewTokens() gets a fresh access/id token with the new selectedcustomerid claim, and
      // the full navigation remounts the provider tree with it.
      await renewTokens();
      window.location.href = '/';
    } catch (error) {
      errorHandler.handleApiError(error, 'Failed to set company.');
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getCompaniesForMe();
        if (cancelled) return;
        const sorted = list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCompanies(sorted);
        if (sorted.length === 1 && sorted[0].activationStatus !== ACTIVATION_STATUS_PENDING) {
          await handleSelect(sorted[0].customerId);
          return;
        }
      } catch (error) {
        if (!cancelled) errorHandler.handleApiError(error, 'Failed to load companies.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = search.trim()
    ? companies.filter((c) => (c.name || '').toLowerCase().includes(search.trim().toLowerCase())
        || (c.orgNumber || '').toLowerCase().includes(search.trim().toLowerCase()))
    : companies;

  return (
    <div className="max-w-lg mx-auto py-10">
      <h1 className="text-2xl font-bold">Select a company</h1>
      <p className="text-gray-600 mt-1">Choose the organization you want to work in.</p>

      {loading ? (
        <p className="mt-6 text-gray-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-gray-500">
          {companies.length === 0 ? 'No companies are connected to your account.' : 'No companies match your search.'}
        </p>
      ) : (
        <>
          {companies.length > 5 && (
            <input
              type="text"
              className="mt-4 w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Search companies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <ul className="auth-company-list mt-4 divide-y divide-gray-200 border border-gray-200 rounded">
            {filtered.map((item) => (
              <li key={item.customerId}>
                <button
                  type="button"
                  className="auth-company-item w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => handleSelect(item.customerId)}
                  disabled={submitting}
                >
                  <span>
                    <span className="block font-medium">{item.name}</span>
                    <span className="block text-sm text-gray-500">Org number: {item.orgNumber}</span>
                  </span>
                  {item.activationStatus === ACTIVATION_STATUS_PENDING && (
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-1">
                      Pending approval
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-6">
        <ButtonV2 label="Back" type={CoreButtonTypes.secondary} onClick={() => window.location.href = '/'} />
      </div>
    </div>
  );
}
